import React, {FC, useEffect, useMemo, useRef, useState} from "react";
import {Badge, Button, Col, Container, FormCheck, FormSelect, InputGroup, Row, Tab, Tabs} from "react-bootstrap";
import calculateReport, {
    RREmployeeReportItemType,
    RRReportItemsType,
    RRReportItemType,
    RRReportType
} from "./ReportComputing";
import {MaybeDate} from "../../types/Maybe";
import {useAppSelector} from "../../hooks";
import {selectAllEvents,} from "../../../features/AppDataSlice";
import {
    dateToString, dateWithMaxTime,
    dateWithZeroTime,
    getDatesInPeriod, minutesToHoursAsShortString,
    showErrorsListInToast, zeroToEmptyStringAsCurrency,
} from "../../tools";
import ReactDatePicker from "react-datepicker";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendar, faClock, faFileExcel, faInfo, faInfoCircle, faMoneyBill} from "@fortawesome/free-solid-svg-icons";
import RRTable from "../../components/rr-table/RRTable";
import CenteredColumn from "../../components/CenteredColumn";
import {collection, getDocs, orderBy, query, where} from "firebase/firestore";
import {appFirestore} from "../../../index";
import {RREmployeesType, RREmployeeType} from "../../types/RREmployeeType";
import {employeesDataConverter} from "../../../firebase/FirebaseEmployeeLoader";
import {RREventsType, RREventType} from "../../types/RREventType";
import {selectCurrentEmployee, selectDepartment} from "../../../features/AppStateSlice";
import {utils, writeFile} from 'xlsx';

type ReportMonthOption = {
    label: string;
    value: string;
    periodFrom: Date;
    periodTo: Date;
}

const BG_MONTH_NAMES = [
    "януари",
    "февруари",
    "март",
    "април",
    "май",
    "юни",
    "юли",
    "август",
    "септември",
    "октомври",
    "ноември",
    "декември",
];

function createMonthOption(baseDate: Date): ReportMonthOption {
    const periodFrom = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    const periodTo = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);

    return {
        label: `${BG_MONTH_NAMES[periodFrom.getMonth()]}, ${periodFrom.getFullYear()}`,
        value: `${periodFrom.getFullYear()}-${String(periodFrom.getMonth() + 1).padStart(2, "0")}`,
        periodFrom,
        periodTo,
    };
}

function getMonthOptions(today: Date): ReportMonthOption[] {
    return Array.from({length: 13}, (_, index) => {
        const optionDate = new Date(today.getFullYear(), today.getMonth() - index, 1);
        return createMonthOption(optionDate);
    });
}


function ItemDescriptionRenderer(props: { item: string }) {
    try {
        const items: { name: string, minutes: number, price: number }[] = JSON.parse(props.item);
        return (
            <>
                {
                    items.map(
                        (i, idx) => <div key={idx} className={"text-nowrap"}>
                            {i.name} [{minutesToHoursAsShortString(i.minutes)}]
                        </div>
                    )
                }
            </>
        );
    } catch (e) {
        return <div>{props.item}</div>;
    }
}

function ReportCellTime(props: { item: RRReportItemType, isTotal?: boolean }) {
    const [showDetails, setShowDetails] = useState(false);
    useAppSelector(selectCurrentEmployee);
    useAppSelector(selectDepartment);
    const item = props.item;
    const total = item.minutesAsHoliday + item.minutesAsSupervisor + item.minutes;
    return (
        <Row className={"m-1"}>
            {
                total > 0 &&
                <Col xs={"12"} className={"p-1 border border-secondary rounded text-nowrap cursor-pointer" +
                    ((total > 1440 && props.isTotal === false) ? " bg-danger text-light" : "")}
                     onClick={event => setShowDetails(!showDetails)}
                >
                    <FontAwesomeIcon icon={faClock}/>&nbsp;
                    {minutesToHoursAsShortString(total)}
                    {
                        item.description &&
                        <FontAwesomeIcon icon={faInfo} className={"float-end me-2 text-info"}/>
                    }
                </Col>
            }
            {
                showDetails && <>
                    {
                        item.minutesAsSupervisor > 0 &&
                        <Col xs={"12"} className={"p-1 ps-2 text-nowrap"}>
                            <Badge>СУП</Badge>&nbsp;
                            {minutesToHoursAsShortString(item.minutesAsSupervisor)}
                        </Col>
                    }
                    {
                        item.minutesAsHoliday > 0 &&
                        <Col xs={"12"} className={"p-1 ps-2 text-nowrap"}>
                            <Badge bg={"success"}>ПОЧ</Badge>&nbsp;
                            {minutesToHoursAsShortString(item.minutesAsHoliday)}
                        </Col>
                    }
                    {
                        item.minutes > 0 &&
                        <Col xs={"12"} className={"p-1 ps-2 text-nowrap"}>
                            <Badge bg={"secondary"}>МОН</Badge>&nbsp;
                            {minutesToHoursAsShortString(item.minutes)}
                        </Col>
                    }
                    {
                        item.description &&
                        <Col xs={"12"} className={"p-1 ps-2"}>
                            <ItemDescriptionRenderer item={item.description}/>
                        </Col>
                    }
                </>
            }
        </Row>
    );
}

function ReportCellPrice(props: { item: RRReportItemType }) {
    const [showDetails, setShowDetails] = useState(false);
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const canSeePayment = currentEmployee?.isSuperAdmin || currentEmployee?.canSeePayment;

    const item = props.item;
    const total = item.minutesAsHoliday + item.minutesAsSupervisor + item.minutes;
    return (
        <Row className={"m-1"}>
            {
                canSeePayment &&
                <Col xs={"12"} className={"p-1 border border-secondary rounded text-nowrap"}>
                    <FontAwesomeIcon icon={faMoneyBill}/> {zeroToEmptyStringAsCurrency(item.price, '')}
                </Col>
            }
        </Row>
    );
}

function EmployeeRow(props: {
    dates: string[],
    report: RRReportType | undefined,
    employee: RREmployeeType,
    mode: string,
    asSubRows?: boolean
}) {
    const report = props.report;
    const empl = props.employee;
    const dates = props.dates;
    const total: RRReportItemType = {
        minutes: 0, minutesAsSupervisor: 0, minutesAsHoliday: 0, price: 0, date: ""
    };
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const activeDepartment = useAppSelector(selectDepartment).toUpperCase();

    const descriptions: {
        name: string;
        minutes: number;
        price: number;
    }[] = [];

    function isFromActiveDepartment(employee: RREmployeeType): boolean {
        if (activeDepartment === "CATERING" && employee.isCatering) return true;
        if (activeDepartment === "MONTAGE" && employee.isMontage) return true;
        if (activeDepartment === "STORE" && employee.isStore) return true;
        return false;
    }

    dates.forEach(
        d => {
            const item = report?.report.employees.find(e => e?.employee.id === empl.id && e?.date === d);
            if (item && isFromActiveDepartment(item.employee)) {
                total.price += item.price;
                total.minutesAsHoliday += item.minutesAsHoliday;
                total.minutesAsSupervisor += item.minutesAsSupervisor;
                total.minutes += item.minutes;
                if (item.description) descriptions.push(
                    {
                        name: item.description,
                        minutes: item.minutes,
                        price: item.price
                    }
                );
            }
        }
    );

    if (total.price > 0) {
        total.description = JSON.stringify(descriptions);
    }

    const bgStyle = props.asSubRows ? {backgroundColor: "white", width: "75px"} : {
        backgroundColor: "antiquewhite",
        width: "75px"
    };

    const tr = (
        <tr>
            <th className={"min-width-200px lh-2"} style={bgStyle}>
                {empl.name}
            </th>
            {
                props.mode === "FULL" &&
                dates.map(
                    d => {
                        let element: JSX.Element;
                        const item = report?.report.employees.find(e => e?.employee.id === empl.id && e?.date === d);

                        if (item) {
                            element = <>
                                <td key={d + "_0_" + empl.id}>
                                    {currentEmployee?.isSuperAdmin ? <ReportCellTime item={item} isTotal={false}/> : ""}
                                </td>
                                <td key={d + "_1_" + empl.id}><ReportCellPrice item={item}/></td>
                            </>
                        } else element = (<>
                            <td key={d + "_0_" + empl.id}/>
                            <td key={d + "_1_" + empl.id}/>
                        </>);

                        return element;
                    }
                )
            }
            <th style={bgStyle}>
                <ReportCellTime item={total}/>
            </th>
            <th style={bgStyle}>
                <ReportCellPrice item={total}/>
            </th>
        </tr>);

    return (total.price > 0) ? tr : null;
}

function getEventName(event: RREventType): string {
    if (event.eventNumber === "HOLIDAY") {
        return "ОТПУСКА";
    } else {
        return `[ ${event.eventNumber} ] ${event.eventName}`;
    }
}

function EventRow(props: {
    dates: string[],
    report: RRReportType | undefined,
    event: RREventType,
    mode: string,
    asHeader: boolean,
    isSuperAdmin: boolean,
}) {
    const report = props.report;
    const event = props.event;
    const dates = props.dates;
    const total: RRReportItemType = {
        minutes: 0, minutesAsSupervisor: 0, minutesAsHoliday: 0, price: 0, date: ""
    };
    const cellBgStyle = props.asHeader ? {backgroundColor: "antiquewhite", width: "75px"} : {width: "75px"};

    dates.forEach(
        d => {
            const item = report?.report.events.find(e => e?.event.eventNumber === event.eventNumber && e?.date === d);
            if (item) {
                total.price += item.price;
                total.minutesAsHoliday += item.minutesAsHoliday;
                total.minutesAsSupervisor += item.minutesAsSupervisor;
                total.minutes += item.minutes;
            }
        }
    )

    const tr = (
        <tr>
            <th className={"min-width-200px"} style={{backgroundColor: "antiquewhite"}}>
                {getEventName(event)}
            </th>
            {
                props.mode === "FULL" &&
                dates.map(
                    d => {
                        const item = report?.report.events.find(e => e?.event.eventNumber === event.eventNumber && e?.date === d);
                        if (item) {
                            return <>
                                <td key={d + "_0_" + event.id} style={cellBgStyle}>
                                    <ReportCellTime item={item}/>
                                </td>
                                <td key={d + "-1_" + event.id} style={cellBgStyle}>
                                    <ReportCellPrice item={item}/>
                                </td>
                            </>
                        } else return <>
                            <td key={d + "_0_" + event.id} style={cellBgStyle}/>
                            <td key={d + "_1_" + event.id} style={cellBgStyle}/>
                        </>;
                    }
                )
            }
            <th style={{backgroundColor: "antiquewhite"}}>
                <ReportCellTime item={total}/>
            </th>
            <th style={{backgroundColor: "antiquewhite"}}>
                <ReportCellPrice item={total}/>
            </th>
        </tr>);

    return (total.price > 0) ? tr : null;
}

function TotalRow(props: { dates: string[], report: RRReportType | undefined, mode: string, isSuperAdmin: boolean, }) {
    const report = props.report;
    const dates = props.dates;
    const totals: RRReportItemsType = [];
    const total: RRReportItemType = {
        minutes: 0, minutesAsSupervisor: 0, minutesAsHoliday: 0, price: 0, date: ""
    };

    report?.report.employees.forEach(
        r => {
            let item = totals.find(t => t.date === r.date);
            if (!item) {
                item = {
                    date: r.date, minutesAsHoliday: 0, minutesAsSupervisor: 0, price: 0, minutes: 0
                }
                totals.push(item);
            }
            item.price += r.price;
            item.minutes += r.minutes;
            item.minutesAsSupervisor += r.minutesAsSupervisor;
            item.minutesAsHoliday += r.minutesAsHoliday;

            total.price += r.price;
            total.minutesAsHoliday += r.minutesAsHoliday;
            total.minutesAsSupervisor += r.minutesAsSupervisor;
            total.minutes += r.minutes;
        }
    )

    const tr = (
        <tr>
            <th className={"min-width-200px lh-2em"} style={{backgroundColor: "antiquewhite"}}>
                ВСИЧКО
            </th>
            {
                props.mode === "FULL" &&
                dates.map(
                    d => {
                        const item = totals.find(t => t.date === d);
                        if (item) {
                            return <>
                                <th key={"total_0_" + d} style={{backgroundColor: "antiquewhite"}}>
                                    <ReportCellTime item={item}/>
                                </th>
                                <th key={"total_1_" + d} style={{backgroundColor: "antiquewhite"}}>
                                    <ReportCellPrice item={item}/>
                                </th>
                            </>
                        } else return <>
                            <th key={"total_0_" + d} style={{backgroundColor: "antiquewhite"}}/>
                            <th key={"total_1_" + d} style={{backgroundColor: "antiquewhite"}}/>
                        </>;
                    }
                )
            }
            <th style={{backgroundColor: "antiquewhite"}}>
                <ReportCellTime item={total}/>
            </th>
            <th style={{backgroundColor: "antiquewhite"}}>
                <ReportCellPrice item={total}/>
            </th>
        </tr>);

    return (total.price > 0) ? tr : null;
}

const ReportTimeView: FC = () => {
    const today = dateWithZeroTime(new Date())!;
    const monthOptions = useMemo(() => getMonthOptions(today), [today]);
    const defaultMonthOption = monthOptions[1] ?? monthOptions[0];

    const department = useAppSelector(selectDepartment);
    const [working, setWorking] = useState(false);
    const [withEmployees, setWithEmployees] = useState(false);
    const [periodFrom, setPeriodFrom] = useState<MaybeDate>();
    const [periodTo, setPeriodTo] = useState<MaybeDate>();
    const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonthOption.value);
    const [dpPeriodFrom, setDpPeriodFrom] = useState<MaybeDate>(defaultMonthOption.periodFrom);
    const [dpPeriodTo, setDpPeriodTo] = useState<MaybeDate>(defaultMonthOption.periodTo);
    const allEvents = useAppSelector(selectAllEvents);
    const [holidayDates, setHolidayDates] = useState<string[] | undefined>();
    const [report, setReport] = useState<RRReportType | undefined>(undefined);
    const [employees, setEmployees] = useState<RREmployeesType | undefined>([]);
    const [viewMode, setViewMode] = useState("EVENT");
    const [viewDetails, setViewDetails] = useState("SHORT");
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const isAdmin = currentEmployee?.isAdmin;
    const employeeId = isAdmin ? undefined : currentEmployee?.id; //"Bv2TJjkY9PSDYCDlciBr";
    // const employeeId = "oF30hCyHHxvBU4kabxeA";
    const tblRef = useRef<HTMLTableElement>(null);

    const allDepartmentEvents = useMemo(() => {
        return allEvents.data
            .filter(event => (department === "catering" && event.isCatering) ||
                (department === "montage" && event.isMontage) ||
                (department === "store" && event.isStore));
    }, [allEvents, department]);

    const events = useMemo(() => {
        const events: RREventsType = [];
        allDepartmentEvents.forEach(
            event => {
                if (events.findIndex(e => e.eventNumber === event.eventNumber) === -1) {
                    events.push(event);
                }
            }
        );
        events.push({
            eventNumber: "HOLIDAY",
            id: "ОТПУСКА",
            clientName: "ОТПУСКА",
            clientEmail: "",
            eventCategory: "MONTAGE",
            isCatering: false,
            eventName: "HOLIDAY",
            eventShortName: "HOLIDAY",
            isDeleted: false,
            isStore: false,
            isMontage: false,
            eventStatus: "ЗАВЪРШЕНО"
        });

        return events;
    }, [allDepartmentEvents]);

    useEffect(() => {
        setHolidayDates(undefined);
        setEmployees(undefined);

        if (periodFrom && periodTo) {
            let _department = "MONTAGE";
            if(department === "store") _department = "WAREHOUSE";
            if(department === "catering") _department = "CETARING";
            getDocs(query(collection(appFirestore, "bankHolidays"),
                where("dateObject", ">=", periodFrom),
                where("dateObject", "<=", periodTo),
                where("department", "==", _department)
            )).then(
                holidays => {
                    setHolidayDates(holidays.docs.map(d => d.data().date))
                }
            );

            try {
                const _where = [
                    where("isDeleted", "==", false),
                    where("isEmployee", "==", true),
                    orderBy(`listIndex_${department}`, "asc")];
                if (department === "catering") {
                    _where.push(where("isCatering", "==", true));
                }
                if (department === "montage") {
                    _where.push(where("isMontage", "==", true));
                }
                if (department === "store") {
                    _where.push(where("isStore", "==", true));
                }

                getDocs(query(collection(appFirestore, "employees"),
                    ..._where
                )).then(
                    employeesSnapshot => setEmployees(employeesDataConverter(employeesSnapshot.docs)
                        // .filter(d => d.id==="4x56jcNJjiuvTcw0r5G0")
                    )
                );
            } catch (e) {
                console.error(e)
            }
        }
    }, [periodFrom, periodTo, department]);

    useEffect(() => {
        setWorking(true);
        if (periodFrom && periodTo && holidayDates && employees) {
            try {
                calculateReport(periodFrom, periodTo, allDepartmentEvents || [], employees, holidayDates, department, employeeId)
                    .then(result => setReport(result));
            } catch (e: any) {
                showErrorsListInToast("Възникна грешка", [e.message]);
                setReport(undefined);
            } finally {
                setWorking(false)
            }
        } else {
            setWorking(false);
        }
    }, [periodFrom, periodTo, events, employees, holidayDates])

    function setRefPeriod() {
        setPeriodFrom(dateWithZeroTime(dpPeriodFrom));
        setPeriodTo(dateWithMaxTime(dpPeriodTo));
    }

    const dates = useMemo(() => {
        if (periodFrom && periodTo) {
            return getDatesInPeriod(periodFrom, periodTo).map(d => dateToString(d));
        } else return [];
    }, [periodFrom, periodTo]);

    function applyMonthSelection(optionValue: string) {
        const selectedOption = monthOptions.find(option => option.value === optionValue);
        if (!selectedOption) {
            return;
        }

        setSelectedMonth(selectedOption.value);
        setDpPeriodFrom(selectedOption.periodFrom);
        setDpPeriodTo(selectedOption.periodTo);
    }

    function exportExcel(): void {
        const workbook = utils.table_to_book(tblRef.current,);
        writeFile(workbook, "Report.xlsx");
    }

    return <Container fluid>
        <h1>Справки и отчети</h1>

        <Row className={"g-2 mt-3 flex-nowrap"}>
            <Col>
                <Row className={"lh-2-5em flex-nowrap"}>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Месец:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <FormSelect
                                    className={"mb-2"}
                                    disabled={working}
                                    value={selectedMonth}
                                    onChange={event => applyMonthSelection(event.target.value)}
                                >
                                    {
                                        monthOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))
                                    }
                                </FormSelect>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Начална дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"periodFromDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodFrom}
                                        onChange={date => setDpPeriodFrom(date || defaultMonthOption.periodFrom)}
                                        value={dateToString(dpPeriodFrom)}
                                        maxDate={dpPeriodTo}
                                    />
                                    <label htmlFor={"periodFromDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Крайна дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"periodToDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodTo}
                                        onChange={date => setDpPeriodTo(date || today)}
                                        value={dateToString(dpPeriodTo)}
                                        minDate={dpPeriodFrom}
                                    />
                                    <label htmlFor={"periodToDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        {
                            !working && <>
                                <Button onClick={setRefPeriod}>Изчисли</Button>&nbsp;
                                <Button onClick={() => exportExcel()} variant={"outline-success"}><FontAwesomeIcon
                                    icon={faFileExcel}/></Button>&nbsp;
                            </>
                        }
                    </Col>
                </Row>
            </Col>
        </Row>

        {
            report &&
            <Row className={"g-2 mt-3"}>
                <Col xs={"12"}>
                    <Tabs activeKey={viewMode} onSelect={eventKey => setViewMode(eventKey || "EVENT")}>
                        <Tab title={"Отчет по събития"} eventKey={"EVENT"}/>
                        {currentEmployee?.isSuperAdmin && <Tab title={"Отчет по служители"} eventKey={"EMPL"}/>}
                    </Tabs>
                </Col>
            </Row>
        }

        {
            report &&
            <Row className={"g-2 mt-3"}>
                <Col xs={"auto"}>
                    <Tabs activeKey={viewDetails} variant={"pills"}
                          onSelect={eventKey => setViewDetails(eventKey || "SHORT")}>
                        <Tab title={"Обобщена за периода"} eventKey={"SHORT"}/>
                        <Tab title={"По дни за периода"} eventKey={"FULL"}/>
                    </Tabs>
                </Col>
                <Col className={"ms-2 fw-bold"}>
                    {
                        viewMode === "EVENT" && currentEmployee?.isSuperAdmin && <FormCheck
                            checked={withEmployees}
                            onChange={event => setWithEmployees(event.target.checked)}
                            label={"Покажи и служителите"}
                        />
                    }
                </Col>
                <Col className={"fw-bold text-end"}>
                    <FontAwesomeIcon icon={faInfoCircle}/>&nbsp;Натиснете полето с времето, за да видите
                    разппределението му по групи.
                </Col>
            </Row>
        }
        <Row className={"g-2 mt-3"}>
            <Col xs={viewDetails === "FULL" ? "12" : "4"}>
                {
                    report ?
                        <RRTable ref={tblRef} autoHeight={true} bottomOffset={48}>
                            <thead>
                            <tr>
                                <CenteredColumn header colSpan={dates.length * 2 + 3}>
                                    <div className={"m-2 fw-bold"}>
                                        {viewMode === "EMPL" ? "Отчет по служители " : "Отчет по събития"} за периода
                                        от {dateToString(periodFrom)} до {dateToString(periodTo)}
                                    </div>
                                </CenteredColumn>
                            </tr>
                            <tr>
                                <th colSpan={dates.length * 2 + 3} className={"bg-light"}></th>
                            </tr>
                            <tr>
                                <th className={"w-160px"}>Служител / събитие</th>
                                {
                                    viewDetails === "FULL" &&
                                    dates.map(
                                        date => <CenteredColumn header={true} key={date} colSpan={2}>
                                            {date}
                                            {
                                                holidayDates?.includes(date) &&
                                                <span className={"float-end"}>
                                            <Badge>H</Badge>
                                        </span>
                                            }
                                        </CenteredColumn>
                                    )
                                }
                                <CenteredColumn header={true} className={"w-100px"} colSpan={2}>Всичко</CenteredColumn>
                            </tr>

                            </thead>

                            <tbody>
                            {viewMode === "EMPL" && employees?.map(empl => <EmployeeRow key={empl.id} dates={dates}
                                                                                        employee={empl}
                                                                                        mode={viewDetails.toString()}
                                                                                        report={report}/>)}
                            {viewMode === "EVENT" && events.map(event => {
                                const rows: any[] = [];
                                const eventRow = <EventRow key={event.id} dates={dates}
                                                           event={event}
                                                           mode={viewDetails.toString()}
                                                           asHeader={withEmployees}
                                                           report={report}
                                                           isSuperAdmin={currentEmployee?.isSuperAdmin ?? false}
                                />;
                                if (eventRow) {
                                    rows.push(eventRow);

                                    if (withEmployees) {
                                        const eventReport: RRReportType = {
                                            periodFrom: report.periodFrom,
                                            periodTo: report.periodTo,
                                            report: {
                                                events: [],
                                                employees: [],
                                            }
                                        }

                                        report.report.events.forEach(
                                            e => {
                                                if (e.event.eventNumber === event.eventNumber) {
                                                    eventReport.report.employees.push(...e.employees);
                                                }
                                            }
                                        )

                                        employees?.forEach(
                                            empl => {
                                                const row = <EmployeeRow key={`${event.id}_${empl.id}`}
                                                                         dates={dates}
                                                                         employee={empl}
                                                                         mode={viewDetails.toString()}
                                                                         asSubRows
                                                                         report={eventReport}/>;
                                                if (row) rows.push(row);
                                            }
                                        )
                                    }

                                    return rows;
                                }
                            })}
                            <TotalRow dates={dates} report={report} mode={viewDetails.toString()}
                                      isSuperAdmin={currentEmployee?.isSuperAdmin ?? true}/>
                            </tbody>
                        </RRTable>
                        : <strong>Моля, изберете период и натиснете "Изчисли"...</strong>
                }
            </Col>
        </Row>

    </Container>
}

export default ReportTimeView;
